/* eslint-disable react/prop-types */
import {useContext, useState} from 'react'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import AtomToast, {atomToastAutoCloseTimes} from '@s-ui/react-atom-toast'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import {useLocation, useRouter} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'
import LayoutPageContent from '@adv-mt/mt-layout-page-content'
import ModalTransactionCancel from '@adv-mt/mt-modal-transaction-cancel'
import NavigationRequireAuth from '@adv-mt/mt-navigation-require-auth'
import IconAngleLeft from '@adv-mt/mt-svg-icons/lib/AngleLeft.js'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'
import IconCloseCircle from '@adv-mt/mt-svg-icons/lib/CloseCircleOutline.js'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {ROUTES} from '../../../config/index.js'
import TransactionTrustFooter from '../components/transactionTrustFooter.js'
import HelpText from './components/HelpText.js'
import {ModalReject} from './components/ModalReject.js'
import {getInitialProps} from './getInitialProps.js'
const TransactionPriceConfirm = ({
  adId,
  adPrice,
  adTitle,
  agreedPrice,
  buyerId,
  pageTrackingData,
  sellerId,
  sellerName = '',
  __HTTP__ = {}
}) => {
  const {redirectTo} = __HTTP__

  const {domain, i18n} = useContext(Context)

  const {pathname, search, query} = useLocation()
  const {push, replace} = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [showModalCancel, setShowModalCancel] = useState(false)
  const [showModalReject, setShowModalReject] = useState(false)
  const [showNotification, setShowNotification] = useState(false)

  const config = domain.get('config')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')

  const {AlreadyCancelledTradeTransactionsError} = config.get('COMMON').ERRORS

  const {tradeId} = query

  const {page, track} = useAnalyticsTrack()

  useMount(() => {
    page(pageTrackingData)
  })

  if (redirectTo) {
    push(redirectTo)
    return <></>
  }

  const isMobile = config.get('isMobile')

  const {META_ROBOTS_NO_INDEX_NO_FOLLOW} = config.get('seo')
  const helmet = {
    meta: [META_ROBOTS_NO_INDEX_NO_FOLLOW]
  }

  const handleOnModalClose = showNotificationError => {
    setShowModalCancel(false)
    showNotificationError && setShowNotification(true)
  }

  const handleOnModalCloseReject = showNotificationError => {
    setShowModalReject(false)
    showNotificationError && setShowNotification(true)
  }

  const handleOnClickCancelCta = () => {
    setShowModalCancel(false)
  }

  const handleOnClickTopbarCta = async () => {
    setShowModalCancel(true)
  }
  const topbarCtaText = i18n.t('COMMON.CANCEL_PURCHASE')

  const handleOnClickCta = async () => {
    setIsLoading(true)

    const [error] = await domain.get('confirm_agreed_price_trade_transactions_use_case').execute({agreedPrice, tradeId})

    setIsLoading(false)

    if (error instanceof AlreadyCancelledTradeTransactionsError) {
      push(ROUTES.USER.MESSAGES)
      return
    }

    if (error) {
      return
    }

    domain
      .get('get_event_tracking_data_use_case')
      .execute({
        event: TRACKING_EVENT_NAMES.BUYER_PAYCAR_PRICE_CONFIRMED_TRANSACTION,
        ad: {id: adId, adPrice, agreedPrice}
      })
      .then(eventTrackingData => {
        track(eventTrackingData)
      })

    const state = {showTransactionPriceConfirmedModal: true, adPrice, agreedPrice}

    const [conversationIdError, response] = await domain
      .get('get_messaging_conversation_id_user_use_case')
      .execute({entityId: adId, buyerId, sellerId})

    if (conversationIdError !== null) {
      replace({
        pathname: ROUTES.USER.MESSAGES,
        state
      })
    }
    replace({
      pathname: `/user/messages/#/id/${response.conversationId}`,
      state
    })
  }

  const handleOnClickRejectCta = () => {
    setShowModalReject(true)
  }

  return (
    <NavigationRequireAuth currentPath={`${pathname}${search}`}>
      <Helmet {...helmet} />
      <Layout.FullWidth
        linkFactory={LinkFactory}
        footerProps={{isSimple: true}}
        topbarProps={{
          customContent: (
            <AtomButton
              color={atomButtonColors.PRIMARY}
              design={atomButtonDesigns.SOLID}
              disabled={false}
              onClick={handleOnClickTopbarCta}
              shape={atomButtonShapes.CIRCULAR}
              size={isMobile ? atomButtonSizes.SMALL : atomButtonSizes.MEDIUM}
            >
              {topbarCtaText}
            </AtomButton>
          ),
          mainNavigationItems: [],
          navButton: (
            <AtomButton
              color="primary"
              design={atomButtonDesigns.FLAT}
              leftIcon={<IconAngleLeft />}
              link
              onClick={() => window.history.back()}
              size={atomButtonSizes.LARGE}
              shape={atomButtonShapes.CIRCULAR}
            />
          ),
          showBrandIcon: true,
          showNavUserOnDesktop: false,
          showToggleNavOnMobile: false,
          ...(!isMobile && {title: `${i18n.t('COMMON.SECURE_PURCHASE_OF')} ${adTitle}`})
        }}
      >
        <Layout.Container isFullHeight isFullWidth isMarginless>
          <LayoutPageContent
            buttonsConfig={{
              primaryCta: {
                color: atomButtonColors.ACCENT,
                design: atomButtonDesigns.SOLID,
                handleOnClick: handleOnClickCta,
                isLoading,
                text: i18n.t('COMMON.CONFIRM_PRICE')
              },
              secondaryCta: {
                color: atomButtonColors.PRIMARY,
                design: atomButtonDesigns.OUTLINE,
                handleOnClick: handleOnClickRejectCta,
                text: i18n.t('COMMON.REJECT_PRICE')
              }
            }}
            footer={<TransactionTrustFooter />}
            helpBox={<HelpText sellerName={sellerName} />}
            stepperConfig={{
              steps: 5,
              step: 2,
              labels: ['', i18n.t('VEHICLE_TRANSACTION.CONFIRM_PRICE.STEP'), '', '', '']
            }}
            subtitle={i18n.t('VEHICLE_TRANSACTION.CONFIRM_PRICE.TITLE')}
          >
            <div className="mt-UserTransactionPriceConfirm-mainContent">
              <TitleBasic
                as={titleBasicTags.h3}
                color={titleBasicColors.black}
                size={titleBasicSizes.xs}
                title={i18n.t('VEHICLE_TRANSACTION.CONFIRM_PRICE.MAIN_TEXT', {sellerName})}
              />
              <div className="mt-UserTransactionPriceConfirm-mainContentPriceWrapper">
                <div className="mt-UserTransactionPriceConfirm-mainContentAdPrice">
                  <span className="mt-UserTransactionPriceConfirm-mainContentAdPriceLabel">
                    {i18n.t('VEHICLE_TRANSACTION.CONFIRM_PRICE.AD_PRICE')}
                  </span>
                  <span className="mt-UserTransactionPriceConfirm-mainContentAdPriceValue">{i18n.c(adPrice)}</span>
                </div>
                <div className="mt-UserTransactionPriceConfirm-mainContentAgreedPrice">
                  <span className="mt-UserTransactionPriceConfirm-mainContentAgreedPriceLabel">
                    {i18n.t('VEHICLE_TRANSACTION.CONFIRM_PRICE.AGREED_PRICE')}
                  </span>
                  <span className="mt-UserTransactionPriceConfirm-mainContentAgreedPriceValue">
                    {agreedPrice ? i18n.c(agreedPrice) : null}
                  </span>
                </div>
              </div>
            </div>
          </LayoutPageContent>
          {showModalCancel && (
            <ModalTransactionCancel
              onCtaClick={handleOnClickCancelCta}
              onModalClose={handleOnModalClose}
              targetPath={ROUTES.USER.MESSAGES}
            />
          )}
          {showModalReject && <ModalReject onModalClose={handleOnModalCloseReject} sellerName={sellerName} />}
          {showNotification && (
            <AtomToast
              autoCloseTime={atomToastAutoCloseTimes.long}
              iconClose={<IconClose size={atomIconSizes.medium} />}
              onClose={() => setShowNotification(false)}
              show
            >
              <div className="mt-UserTransactionPriceConfirm-notification">
                <IconCloseCircle size="medium" />
                <p className="mt-UserTransactionPriceConfirm-notificationText">
                  {i18n.t('ERROR_NOTIFICATION_CANCEL_TRADE')}
                </p>
              </div>
            </AtomToast>
          )}
        </Layout.Container>
      </Layout.FullWidth>
    </NavigationRequireAuth>
  )
}

TransactionPriceConfirm.displayName = 'TransactionPriceConfirm'
TransactionPriceConfirm.propTypes = {}

TransactionPriceConfirm.getInitialProps = getInitialProps

export default TransactionPriceConfirm
